import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { mdiArrowDownBold, mdiRedo, mdiUndo } from "@mdi/js";
import Icon from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { MoneyManagerData, MoneyManagerParticipantEntry } from "api/money-manager";
import { GetUserById } from "api/profile";
import { noSpinnerSx } from "utils/sx";

function getUnitPrice(catId: string, data: MoneyManagerData, participantIds: string[]): number | null {
  const cat = data.categories.find((c) => c.id === catId);
  if (!cat || cat.type !== "per_unit") return null;
  const total = parseFloat(cat.total) || 0;
  const sumUnits = participantIds.reduce(
    (sum, id) => sum + (parseFloat(data.entries[id]?.categories[catId] as string) || 0),
    0
  );
  return sumUnits > 0 ? total / sumUnits : null;
}

function getAddonPrice(catId: string, data: MoneyManagerData, participantIds: string[]): number {
  const cat = data.categories.find((c) => c.id === catId);
  if (!cat || cat.type !== "addon") return 0;
  const total = parseFloat(cat.total) || 0;
  const yesCount = participantIds.filter((id) => !!data.entries[id]?.categories[catId]).length;
  return yesCount > 0 ? total / yesCount : 0;
}

function getOrCreateEntry(data: MoneyManagerData, profileId: string): MoneyManagerParticipantEntry {
  return data.entries[profileId] ?? { categories: {}, advance: "", deduction: "", paid: false };
}

export interface DataEntryProps {
  data: MoneyManagerData;
  isAdmin: boolean;
  participants: GetUserById[];
  onSave: (data: MoneyManagerData) => void;
}

interface BulkFillRowProps {
  data: MoneyManagerData;
  participantIds: string[];
  bulkUpdateCategory: (catId: string, value: string | boolean) => void;
  bulkUpdateField: (field: "advance" | "deduction", value: string) => void;
}

function BulkFillRow({ data, participantIds, bulkUpdateCategory, bulkUpdateField }: BulkFillRowProps) {
  const [bulkValues, setBulkValues] = useState<Record<string, string>>({});

  function applyBulk(key: string, type: "per_unit" | "addon" | "advance" | "deduction") {
    const value = bulkValues[key] ?? "";
    if (type === "per_unit") {
      bulkUpdateCategory(key, value);
    } else if (type === "addon") {
      const allChecked = participantIds.every((id) => !!data.entries[id]?.categories[key]);
      bulkUpdateCategory(key, !allChecked);
    } else {
      bulkUpdateField(type, value);
    }
  }

  return (
    <TableRow sx={{ bgcolor: "action.hover" }}>
      <TableCell
        sx={(theme) => ({
          position: "sticky",
          left: 0,
          zIndex: 2,
          backgroundColor: "background.paper",
          backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
          boxShadow: `inset -1px 0 0 0 ${theme.palette.divider}`,
        })}
      >
        <Typography variant='caption' fontWeight={700} color='text.secondary' noWrap>
          Bulk fill
        </Typography>
      </TableCell>
      {data.categories.map((cat) => (
        <TableCell key={cat.id}>
          {cat.type === "per_unit" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: 110 }}>
              <TextField
                size='small'
                type='number'
                inputProps={{ min: 0 }}
                placeholder='value'
                value={bulkValues[cat.id] ?? ""}
                onChange={(e) => setBulkValues((v) => ({ ...v, [cat.id]: e.target.value }))}
                sx={{ "& input": { py: 0.5, px: 1 }, ...noSpinnerSx, flex: 1 }}
              />
              <Tooltip title='Apply to all' arrow>
                <IconButton size='small' onClick={() => applyBulk(cat.id, "per_unit")}>
                  <Icon path={mdiArrowDownBold} size={0.7} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title='Toggle all' arrow>
              <Checkbox
                size='small'
                checked={participantIds.every((id) => !!data.entries[id]?.categories[cat.id])}
                indeterminate={
                  participantIds.some((id) => !!data.entries[id]?.categories[cat.id]) &&
                  !participantIds.every((id) => !!data.entries[id]?.categories[cat.id])
                }
                onChange={() => applyBulk(cat.id, "addon")}
              />
            </Tooltip>
          )}
        </TableCell>
      ))}
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: 110 }}>
          <TextField
            size='small'
            type='number'
            inputProps={{ min: 0 }}
            placeholder='value'
            value={bulkValues.advance ?? ""}
            onChange={(e) => setBulkValues((v) => ({ ...v, advance: e.target.value }))}
            sx={{ "& input": { py: 0.5, px: 1 }, ...noSpinnerSx, flex: 1 }}
          />
          <Tooltip title='Apply to all' arrow>
            <IconButton size='small' onClick={() => applyBulk("advance", "advance")}>
              <Icon path={mdiArrowDownBold} size={0.7} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: 110 }}>
          <TextField
            size='small'
            type='number'
            inputProps={{ min: 0 }}
            placeholder='value'
            value={bulkValues.deduction ?? ""}
            onChange={(e) => setBulkValues((v) => ({ ...v, deduction: e.target.value }))}
            sx={{ "& input": { py: 0.5, px: 1 }, ...noSpinnerSx, flex: 1 }}
          />
          <Tooltip title='Apply to all' arrow>
            <IconButton size='small' onClick={() => applyBulk("deduction", "deduction")}>
              <Icon path={mdiArrowDownBold} size={0.7} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

const MAX_HISTORY = 50;

function useHistory(data: MoneyManagerData, onSave: (d: MoneyManagerData) => void) {
  const pastRef = useRef<MoneyManagerData[]>([]);
  const futureRef = useRef<MoneyManagerData[]>([]);
  const [, forceUpdate] = useState(0);

  const save = useCallback(
    (updated: MoneyManagerData) => {
      pastRef.current = [...pastRef.current.slice(-(MAX_HISTORY - 1)), data];
      futureRef.current = [];
      forceUpdate((n) => n + 1);
      onSave(updated);
    },
    [data, onSave]
  );

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;
    const previous = pastRef.current[pastRef.current.length - 1];
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [...futureRef.current, data];
    forceUpdate((n) => n + 1);
    onSave(previous);
  }, [data, onSave]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current[futureRef.current.length - 1];
    futureRef.current = futureRef.current.slice(0, -1);
    pastRef.current = [...pastRef.current, data];
    forceUpdate((n) => n + 1);
    onSave(next);
  }, [data, onSave]);

  return { save, undo, redo, canUndo: pastRef.current.length > 0, canRedo: futureRef.current.length > 0 };
}

export default function DataEntry(props: DataEntryProps) {
  const { data, isAdmin, participants, onSave } = props;
  const participantIds = participants.map((p) => String(p.uid));
  const { save, undo, redo, canUndo, canRedo } = useHistory(data, onSave);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!isAdmin) return undefined;
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAdmin, undo, redo]);

  if (participants.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' textAlign='center' py={4}>
        No participants have joined this event yet.
      </Typography>
    );
  }

  function updateEntry(profileId: string, catId: string, value: string | boolean) {
    const entry = getOrCreateEntry(data, profileId);
    const updated: MoneyManagerData = {
      ...data,
      entries: {
        ...data.entries,
        [profileId]: { ...entry, categories: { ...entry.categories, [catId]: value } },
      },
    };
    save(updated);
  }

  function updateParticipantField(profileId: string, field: "advance" | "deduction", value: string) {
    const entry = getOrCreateEntry(data, profileId);
    const updated: MoneyManagerData = {
      ...data,
      entries: { ...data.entries, [profileId]: { ...entry, [field]: value } },
    };
    save(updated);
  }

  const readonly = !isAdmin;

  function bulkUpdateCategory(catId: string, value: string | boolean) {
    const updatedEntries = { ...data.entries };
    for (const pid of participantIds) {
      const entry = getOrCreateEntry(data, pid);
      updatedEntries[pid] = { ...entry, categories: { ...entry.categories, [catId]: value } };
    }
    save({ ...data, entries: updatedEntries });
  }

  function bulkUpdateField(field: "advance" | "deduction", value: string) {
    const updatedEntries = { ...data.entries };
    for (const pid of participantIds) {
      const entry = getOrCreateEntry(data, pid);
      updatedEntries[pid] = { ...entry, [field]: value };
    }
    save({ ...data, entries: updatedEntries });
  }

  return (
    <Box>
      {isAdmin && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 1 }}>
          <Tooltip title='Undo (Ctrl+Z)' arrow>
            <span>
              <IconButton size='small' onClick={undo} disabled={!canUndo}>
                <Icon path={mdiUndo} size={0.8} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Redo (Ctrl+Y)' arrow>
            <span>
              <IconButton size='small' onClick={redo} disabled={!canRedo}>
                <Icon path={mdiRedo} size={0.8} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      <TableContainer component={Paper} variant='outlined'>
        <Table size='small' sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  backgroundColor: "background.paper",
                  boxShadow: (theme) => `inset -1px 0 0 0 ${theme.palette.divider}`,
                }}
              >
                Participant
              </TableCell>
              {data.categories.map((cat) => {
                const pid = cat.id;
                let sub = "";
                if (cat.type === "per_unit") {
                  const price = getUnitPrice(pid, data, participantIds);
                  sub = price != null ? `${price.toFixed(2)} ${data.currency}/unit` : "enter units";
                } else {
                  const price = getAddonPrice(pid, data, participantIds);
                  const yesCount = participantIds.filter((id) => !!data.entries[id]?.categories[pid]).length;
                  sub =
                    yesCount > 0
                      ? `${price.toFixed(2)} ${data.currency}/person (${yesCount} yes)`
                      : `total: ${parseFloat(cat.total || "0").toFixed(2)} ${data.currency}`;
                }
                return (
                  <TableCell key={cat.id} sx={{ fontWeight: 700 }}>
                    {cat.name}
                    <Typography variant='caption' display='block' color='primary'>
                      {sub}
                    </Typography>
                  </TableCell>
                );
              })}
              <TableCell sx={{ fontWeight: 700 }}>
                Advance
                <Typography variant='caption' display='block' color='text.secondary'>
                  {data.currency}
                </Typography>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                Deduction
                <Typography variant='caption' display='block' color='text.secondary'>
                  {data.currency}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isAdmin && (
              <BulkFillRow
                data={data}
                participantIds={participantIds}
                bulkUpdateCategory={bulkUpdateCategory}
                bulkUpdateField={bulkUpdateField}
              />
            )}
            {participants
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
              .map((p) => {
                const entry = getOrCreateEntry(data, String(p.uid));
                return (
                  <TableRow
                    key={p.uid}
                    onPointerDown={(e) => {
                      pointerStartRef.current = { x: e.clientX, y: e.clientY };
                    }}
                    onClick={(e) => {
                      const start = pointerStartRef.current;
                      pointerStartRef.current = null;
                      const target = e.target as HTMLElement;
                      if (target.closest("input, textarea, button, label, .MuiButtonBase-root")) return;
                      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) return;
                      setSelectedRowId(selectedRowId === p.uid ? null : p.uid);
                    }}
                    sx={(theme) => ({
                      cursor: "pointer",
                      WebkitTapHighlightColor: "transparent",
                      backgroundColor: selectedRowId === p.uid ? "action.selected" : "background.paper",
                      "@media (hover: hover)": {
                        "&:hover": {
                          backgroundColor: selectedRowId === p.uid ? "action.selected" : "action.hover",
                        },
                        ...(selectedRowId !== p.uid && {
                          "&:hover .mm-sticky-cell": {
                            backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                          },
                        }),
                      },
                    })}
                  >
                    <TableCell
                      className='mm-sticky-cell'
                      sx={(theme) => ({
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        backgroundColor: "background.paper",
                        boxShadow: `inset -1px 0 0 0 ${theme.palette.divider}`,
                        ...(selectedRowId === p.uid && {
                          backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
                        }),
                      })}
                    >
                      <strong>{p.name}</strong>
                    </TableCell>
                    {data.categories.map((cat) => (
                      <TableCell key={cat.id}>
                        {cat.type === "per_unit" ? (
                          <Box sx={{ width: 80 }}>
                            <TextField
                              size='small'
                              type='number'
                              inputProps={{ min: 0 }}
                              placeholder='0'
                              value={(entry.categories[cat.id] as string) ?? ""}
                              disabled={readonly}
                              onChange={(e) => updateEntry(String(p.uid), cat.id, e.target.value)}
                              sx={{ "& input": { py: 0.5, px: 1 }, ...noSpinnerSx }}
                            />
                          </Box>
                        ) : (
                          <Checkbox
                            size='small'
                            checked={!!entry.categories[cat.id]}
                            disabled={readonly}
                            onChange={(e) => updateEntry(String(p.uid), cat.id, e.target.checked)}
                          />
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Box sx={{ width: 80 }}>
                        <TextField
                          size='small'
                          type='number'
                          inputProps={{ min: 0 }}
                          placeholder='0'
                          value={entry.advance}
                          disabled={readonly}
                          onChange={(e) => updateParticipantField(String(p.uid), "advance", e.target.value)}
                          sx={{ "& input": { py: 0.5, px: 1 }, ...noSpinnerSx }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 80 }}>
                        <TextField
                          size='small'
                          type='number'
                          inputProps={{ min: 0 }}
                          placeholder='0'
                          value={entry.deduction}
                          disabled={readonly}
                          onChange={(e) => updateParticipantField(String(p.uid), "deduction", e.target.value)}
                          sx={{ "& input": { py: 0.5, px: 1 }, ...noSpinnerSx }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
